import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from "uuid";

// OpenLayers
import OlFeature from "ol/Feature";
import OlSourceVector from "ol/source/Vector";
import OlPoint from "ol/geom/Point";
import OlLayerVector from "ol/layer/Vector";
import { LineString as OlLineString } from "ol/geom";
import { getLength as OlSphereLength } from "ol/sphere";
import { transform as OlTransform } from "ol/proj";
import { isEmpty } from 'ol/extent';

// Custom Components
import MapContext from "@/components/Map/MapContext";
import MapRoutingContext from '@/components/MapRoutingContext/MapRoutingContext';
import {
  // drawingRouteStyle,
  // drawRouteStyle,
  routeStyle,
  routePinPointStyle,
  routeDashedLineStyle,
  originalClickPointStyle,
  routePointStyle,
} from "@/components/Map/mapStyles";

// Types
import { MapContextType } from "@/@types/context/MapContext";
import { MapRoutingContexType } from '@/components/MapRoutingContext/MapRoutingContext';
import { DCRecord } from "@/@types/lib/dataController";
import { MarkerPoint } from "@/ui/Charts/ProfileChart";

export type RouteData = {
  record: DCRecord;
  feature: OlFeature<OlLineString>
}

export type RoutePoint = {
  coord: number[];
  label: string;
  uuid: string;
  poi_id?: number;
};

export type RoutePointDataType = {
  route_type: string;
  max: number;
  ord: number;
  x: number;
  y: number;
  z: number;
};

export type RoutingProps = {
  onClearRoute?: () => void;
}

const LINE = "LineString";
const POLY = "Polygon";
const PT = "Point";

function useRouting(props: RoutingProps) {

  const mapContext = useContext(MapContext) as MapContextType;
  // if (!mapContext) {
  //   throw new Error('useRouting must be used within a MapRoutingProvider');
  // }

  const mapRoutingContext = useContext(MapRoutingContext) as MapRoutingContexType;

  const { onClearRoute } = props;

  const { routeData, setRouteData, routingPoints, setRoutingPoints, addLayersToMap } = mapRoutingContext;
  const { clickPointsSourceRef, routePointsSourceRef, routeSourceRef, clickPoint2routePointSourceRef, pointOnRouteSourceRef } = mapRoutingContext;

  

  const mapContextMap = mapContext?.map;

  // register layers on map once it's ready
  useEffect(() => {
    if (mapContext?.map) {
      addLayersToMap(mapContext.map);
    }
  }, [mapContextMap]);


  // clear all layer' sources when component unmounts
  // eslint-disable-next-line arrow-body-style
  // useEffect(() => {
  //   return () => {
  //     clickPointsSourceRef.current.clear();
  //     routePointsSourceRef.current.clear();
  //     routeSourceRef.current.clear();
  //     clickPoint2routePointSourceRef.current.clear();
  //     pointOnRouteSourceRef.current.clear();
  //   }
  // }, []);

  useEffect(() => {
    if (routeData) {

      const pointFeatures = generateRoutePointMarkerFeatures(routeData.record);

      routePointsSourceRef.current.clear();
      routePointsSourceRef.current.addFeatures(pointFeatures);

      routeSourceRef.current.clear();
      routeSourceRef.current.addFeature(routeData.feature);

      // console.log('ue', routeSourceRef.current.getFeatures());
      
      const routePointData = routeData.record.points as RoutePointDataType[];

      const hasRouteThroughAllPoints = 
        routingPoints.length > 0 &&
        routeData &&
        routingPoints.length === routePointData.length;

      if (hasRouteThroughAllPoints) {
        clickPointsSourceRef.current.clear();
        clickPoint2routePointSourceRef.current.clear();

        for (let i = 0; i < routingPoints.length; i += 1) {
          if (routePointData) {
            // this is a point where we clicked/selected
            const point1 = routingPoints[i].coord;
            // this is a point from calculated route
            const point2 = OlTransform(
              [routePointData[i].x, routePointData[i].y],
              "EPSG:3765",
              "EPSG:3857"
            );

            // if two points are the same, continue to next one
            if (JSON.stringify(point1) === JSON.stringify(point2)) {
              // eslint-disable-next-line no-continue
              continue;
            }

            // calculate spherical distance between two points
            const lineString = new OlLineString([point1, point2]);
            const length = OlSphereLength(lineString, { projection: "EPSG:3857" });

            if (length > 10) {
              // add dashed line between click and routed point
              const feature = new OlFeature(lineString);
              clickPoint2routePointSourceRef.current.addFeature(feature);

              // add dot on click location
              const point = new OlPoint(point1);
              const pointFeature = new OlFeature(point);
              pointFeature.setStyle(originalClickPointStyle);
              clickPointsSourceRef.current.addFeature(
                pointFeature
              );
            }
          }
        }
      }
    } else if (routingPoints.length > 0) {
      const features = generateClickPointMarkerFeatures(routingPoints);
      routePointsSourceRef.current.clear();
      routePointsSourceRef.current.addFeatures(features);

      clickPointsSourceRef.current.clear();
      routeSourceRef.current.clear();
      clickPoint2routePointSourceRef.current.clear();
      pointOnRouteSourceRef.current.clear();
    } else {
      clickPointsSourceRef.current.clear();
      routePointsSourceRef.current.clear();
      routeSourceRef.current.clear();
      clickPoint2routePointSourceRef.current.clear();
      pointOnRouteSourceRef.current.clear();
    }
  }, [routeData, routingPoints]);

  const generateRoutePointMarkerFeatures = useCallback((r: DCRecord) => {
    const features = new Array<OlFeature<OlPoint>>();

    if (r?.points) {
      const rPoints = r.points as RoutePointDataType[];

      // eslint-disable-next-line no-restricted-syntax
      for (const [index, point] of Object.entries(rPoints)) {
        const coordinatesTransformed = OlTransform(
          [point.x, point.y],
          "EPSG:3765",
          "EPSG:3857"
        );
        const p = new OlPoint(coordinatesTransformed);

        const feat = new OlFeature<OlPoint>({
          type: PT,
          geometry: p,
        });
        feat.set("index", parseInt(index, 10));
        features.push(feat);
      };
    }

    return features;;
  }, []);

  const generateGraphMarkers = useCallback((r: DCRecord | undefined) => {

    const graphPoints = new Array<MarkerPoint>();

    let length = 0;
    let time = 0;

    if (r?.points) {
      const rPoints = r.points as RoutePointDataType[];

      // eslint-disable-next-line no-restricted-syntax
      for (const [index, point] of rPoints.entries()) {
        graphPoints.push({
          x: length,
          y: point.z,
          index: index + 1,
          time,
        });

        // @ts-ignore
        if (index < r.segments.length) {
          // @ts-ignore
          length += Number(r.segments[index].seg_length);
          // @ts-ignore
          time = r.segments[index].time_min;
        }
      }
    }

    return graphPoints;
  }, []);

  const generateClickPointMarkerFeatures = useCallback((rps: RoutePoint[]) => {
    const features = new Array<OlFeature<OlPoint>>();

    if (rps) {
      rps.forEach((rp, index) => {
        const p = new OlPoint(rp.coord);

        const feat = new OlFeature<OlPoint>({
          type: PT,
          geometry: p,
        });
        feat.set("index", index);
        features.push(feat);
      });
    }

    return features;;
  }, []);

  const loadRoutingPoints = useCallback((newRoutePoints: RoutePoint[]) => {
    setRoutingPoints(newRoutePoints);
  }, []);

  const addToRoute = useCallback((coord: number[] | undefined, label: string, poi_id?: number) => {

    const lastPoint = routingPoints.length > 0 ? routingPoints[routingPoints.length - 1] : null;

    const uuid = uuidv4();

    if (coord) {
      if (lastPoint === null ||
        (Math.abs(lastPoint.coord[0] - coord[0]) > 1 || Math.abs(lastPoint.coord[1] - coord[1]) > 1)) {
        const newRoutingPoints = [...routingPoints, { coord, label, uuid, poi_id }]
        setRoutingPoints(newRoutingPoints);
        localStorage.setItem("route_points", JSON.stringify(newRoutingPoints))
        if (onClearRoute) {
          onClearRoute();
        }
      }
      else {
        setRoutingPoints([...routingPoints]); //trigger open route pane
      }
    }
  }, [routingPoints, onClearRoute]);

  const deleteFromRoute = useCallback((index: number) => {
    const newRoutingPoints: RoutePoint[] = [];

    for (let i = 0; i < routingPoints.length; i += 1) {
      if (i !== index) newRoutingPoints.push(routingPoints[i]);
    }

    setRoutingPoints(newRoutingPoints);
  }, [routingPoints]);


  const clearAllRouteData = useCallback(() => {
    // clickPointsSourceRef.current.clear();
    // routePointsSourceRef.current.clear();
    // routeSourceRef.current.clear();
    // clickPoint2routePointSourceRef.current.clear();
    // pointOnRouteSourceRef.current.clear();

    setRoutingPoints([]);
    setRouteData(null);
  }, []);

  const drawPointOnRoute = useCallback((mrel: number) => {
    const geom = routeData?.feature.getGeometry();

    if (geom) {
      const p = geom.getCoordinateAtM(mrel);
      const pFeatures = pointOnRouteSourceRef.current.getFeatures();
      if (Array.isArray(pFeatures)) {
        if (pFeatures.length === 0) {
          if (p) {
            pointOnRouteSourceRef.current.addFeature(
              new OlFeature<OlPoint>({
                geometry: new OlPoint(p),
              })
            );
          }
        } else {
          const pf = pFeatures[0];
          if (p) pf.setGeometry(new OlPoint(p));
        }
      }
    }
  }, [routeData]);

  const clearPointOnRoute = useCallback(() => {
    pointOnRouteSourceRef.current.clear();
  }, []);

  const clearRoute = useCallback(() => {
    setRouteData(null);
  }, []);

  const drawRoute = useCallback((rd: RouteData) => {
    // console.log('draw route', rd);
    setRouteData(rd);
  }, []);

  // const routeSourceRevision = routeSourceRef.current.getRevision();
  // console.log(routeSourceRef.current.getFeatures());

  const getRouteExtent = () => {
    if (routeSourceRef.current.getExtent !== undefined) {
      const extent = routeSourceRef.current.getExtent();
      if (!isEmpty(extent)) {
        // console.log('returning extent')
        return extent;
      }
    }
    // console.warn('no extent', routeSourceRef.current.getFeatures())
    return undefined;
  };

  const drawFirstPoint = useCallback((rp: RoutePoint) => {
    // console.log('draw first point', rp);
    const feat = new OlFeature({
      type: PT,
      geometry: new OlPoint(rp.coord),
    });
    feat.set("index", 0);

    routePointsSourceRef.current.clear();
    routePointsSourceRef.current.addFeature(feat);
  }, []);

  // console.log('render useRouting');

  // console.log('render',mapContext?.map);
      
  return {
    routingPoints,
    addToRoute,
    deleteFromRoute,
    loadRoutingPoints,
    clearAllRouteData,
    drawPointOnRoute,
    clearPointOnRoute,
    routeData,
    drawRoute,
    clearRoute,
    getRouteExtent,
    drawFirstPoint,
    generateGraphMarkers
  }
}

export default useRouting;
